import { useState } from 'react'
import Card, { CardContent } from './UI/Card'
import Button from './UI/Button'
import { Users, Zap, Database, CheckCircle, Star, Calendar } from 'lucide-react'

export default function PlanSelectionCard({ 
  plan, 
  productModules = [], 
  productFeatures = [], 
  isSelected, 
  onSelect,
  customPrice,
  onCustomPriceChange,
  onContactUsClick
}) {
  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price)
  }

  const calculateSavings = (monthlyPrice, yearlyPrice) => {
    if (!monthlyPrice || !yearlyPrice || monthlyPrice === 0) return null
    const monthlyYearlyTotal = monthlyPrice * 12
    if (yearlyPrice >= monthlyYearlyTotal) return null
    const savings = monthlyYearlyTotal - yearlyPrice
    const savingsPercent = Math.round((savings / monthlyYearlyTotal) * 100)
    return savingsPercent
  }

  // Get features from plan - support both new schema (direct fields) and old schema (features object)
  const getFeatures = () => {
    // New schema: max_users, max_api_calls, max_storage_gb are direct fields
    if (plan.max_users !== undefined || plan.max_api_calls !== undefined || plan.max_storage_gb !== undefined) {
      return {
        max_users: plan.max_users,
        api_requests_per_month: plan.max_api_calls,
        max_storage_gb: plan.max_storage_gb
      }
    }
    // Old schema: features object
    return plan.features || {}
  }

  const features = getFeatures()

  // Get module names from IDs - support both new schema (included_modules) and old schema (module_ids)
  const getModuleNames = () => {
    // Use included_modules (new schema) or module_ids (old schema) for backward compatibility
    // The Plan model maps included_modules to module_ids for backward compatibility
    // Handle both string (JSON) and array formats
    let moduleIds = plan.included_modules || plan.module_ids || []
    
    // Parse if it's a string (JSONB from database might come as string)
    if (typeof moduleIds === 'string') {
      try {
        moduleIds = JSON.parse(moduleIds)
      } catch (e) {
        console.error('PlanSelectionCard: Failed to parse module_ids as JSON', e)
        moduleIds = []
      }
    }
    
    // Ensure it's an array
    if (!Array.isArray(moduleIds)) {
      moduleIds = []
    }
    
    console.log('PlanSelectionCard: Getting module names for plan', plan.name, {
      planId: plan.id,
      included_modules: plan.included_modules,
      module_ids: plan.module_ids,
      parsedModuleIds: moduleIds,
      productModulesCount: productModules?.length || 0,
      productModules: productModules?.map(m => ({ id: m.id, name: m.name })) || []
    })
    
    if (!moduleIds || moduleIds.length === 0) {
      console.log('PlanSelectionCard: No module IDs found in plan', plan.name)
      return []
    }
    if (!productModules || productModules.length === 0) {
      console.log('PlanSelectionCard: No product modules provided', plan.name)
      return []
    }
    
    const moduleNames = moduleIds
      .map(moduleId => {
        // Convert both to strings for comparison (handles UUID string comparison)
        const module = productModules.find(m => String(m.id) === String(moduleId))
        if (module) {
          return module.name
        } else {
          console.log('PlanSelectionCard: Module not found', {
            moduleId,
            moduleIdType: typeof moduleId,
            planName: plan.name,
            availableModuleIds: productModules.map(m => ({ id: String(m.id), name: m.name }))
          })
          return null
        }
      })
      .filter(Boolean)
    
    console.log('PlanSelectionCard: Resolved module names', moduleNames)
    return moduleNames
  }

  // Get feature names from IDs - support both new schema (included_features) and old schema (feature_ids)
  const getFeatureNames = () => {
    // Use included_features (new schema) or feature_ids (old schema) for backward compatibility
    // Handle both string (JSON) and array formats
    let featureIds = plan.included_features || plan.feature_ids || []
    
    // Parse if it's a string (JSONB from database might come as string)
    if (typeof featureIds === 'string') {
      try {
        featureIds = JSON.parse(featureIds)
      } catch (e) {
        console.error('PlanSelectionCard: Failed to parse feature_ids as JSON', e)
        featureIds = []
      }
    }
    
    // Ensure it's an array
    if (!Array.isArray(featureIds)) {
      featureIds = []
    }
    
    if (!featureIds || featureIds.length === 0) return []
    if (!productFeatures || productFeatures.length === 0) return []
    
    return featureIds
      .map(featureId => {
        // Convert both to strings for comparison (handles UUID string comparison)
        const feature = productFeatures.find(f => String(f.id) === String(featureId))
        return feature ? feature.name : null
      })
      .filter(Boolean)
  }

  const moduleNames = getModuleNames()
  const featureNames = getFeatureNames()

  // Get subtitle - show user count or description
  const getSubtitle = () => {
    if (features.max_users && features.max_users > 0) {
      return `${features.max_users} users`
    }
    return plan.description || ''
  }

  return (
    <Card 
      onClick={onSelect}
      className={`h-full flex flex-col relative transition-all duration-200 cursor-pointer ${
        isSelected 
          ? 'ring-2 ring-blue-500 border-blue-500 shadow-lg' 
          : plan.is_popular 
            ? 'border-2 border-blue-500 hover:shadow-lg hover:scale-105' 
            : 'border border-secondary-200 hover:border-blue-300 hover:shadow-lg hover:scale-105'
      }`}
    >
      {/* Most Popular Badge - Top Right Edge */}
      {plan.is_popular && (
        <div className="absolute top-0 right-0 z-10">
          <span className="inline-flex items-center px-2.5 py-1 rounded-bl text-xs font-medium bg-blue-500 text-white">
            Most Popular
          </span>
        </div>
      )}
      <CardContent className="p-6 flex flex-col flex-1">

        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-xl font-bold text-secondary-900">{plan.name}</h3>
              {plan.is_enterprise && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-300 text-white">
                  Enterprise
                </span>
              )}
            </div>
            {plan.description ? (
              <p className="text-sm text-secondary-600 mb-4">{plan.description}</p>
            ) : plan.is_enterprise ? (
              <p className="text-sm text-secondary-600 mb-4">For enterprises that need to scale. Top-tier SLAs, advanced security, white-glove support and more</p>
            ) : null}
          </div>
        </div>

        {/* Pricing */}
        {!plan.is_enterprise && (plan.monthly_price > 0 || plan.yearly_price > 0) && (
          <div className="mb-4">
            {plan.monthly_price > 0 && (
              <>
                <div className="text-2xl font-bold text-secondary-900">
                  {formatPrice(plan.monthly_price, plan.currency)}
                  <span className="text-base font-normal text-secondary-500 ml-1">/month</span>
                </div>
                {plan.yearly_price > 0 && (() => {
                  const savingsPercent = calculateSavings(plan.monthly_price, plan.yearly_price)
                  return (
                    <div className="text-sm text-secondary-600 mt-1">
                      {formatPrice(plan.yearly_price, plan.currency)}/year
                      {savingsPercent && savingsPercent > 0 && (
                        <span className="text-green-600 ml-1">(save {savingsPercent}%)</span>
                      )}
                    </div>
                  )
                })()}
              </>
            )}
            {plan.monthly_price === 0 && plan.yearly_price > 0 && (
              <div className="text-2xl font-bold text-secondary-900">
                {formatPrice(plan.yearly_price, plan.currency)}
                <span className="text-base font-normal text-secondary-500 ml-1">/year</span>
              </div>
            )}
            {/* Best for User recommendation */}
            {plan.is_popular && (
              <p className="text-sm text-secondary-600 mt-2">Best for User</p>
            )}
          </div>
        )}


        {/* Trial Days - Only show when trial_days is greater than 0 */}
        {plan.trial_days != null && Number(plan.trial_days) > 0 && (
          <div className="mb-4 p-2 bg-blue-50 rounded border border-blue-200">
            <div className="flex items-center text-sm">
              <Calendar className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0" />
              <span className="text-blue-900 font-medium">
                {Number(plan.trial_days)} day{Number(plan.trial_days) !== 1 ? 's' : ''} free trial
              </span>
            </div>
          </div>
        )}

        {/* Plan Details Section */}
        <div className="mb-4">
          {/* Limits - Simple display with icons */}
          <div className="space-y-3">
            <div className="flex items-center text-sm">
              <Users className="h-4 w-4 text-secondary-400 mr-2" />
              <span className="text-secondary-600">
                {features.max_users && features.max_users > 0
                  ? `${features.max_users} users`
                  : 'Unlimited users'}
              </span>
            </div>

            <div className="flex items-center text-sm">
              <Zap className="h-4 w-4 text-secondary-400 mr-2" />
              <span className="text-secondary-600">
                {features.api_requests_per_month && features.api_requests_per_month > 0
                  ? `${features.api_requests_per_month.toLocaleString()} API calls/month`
                  : 'Unlimited API calls'}
              </span>
            </div>

            <div className="flex items-center text-sm">
              <Database className="h-4 w-4 text-secondary-400 mr-2" />
              <span className="text-secondary-600">
                {features.max_storage_gb && features.max_storage_gb > 0
                  ? `${features.max_storage_gb} GB storage`
                  : 'Unlimited storage'}
              </span>
            </div>
          </div>
        </div>

        {/* Modules */}
        {moduleNames.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center text-sm mb-2">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
              <span className="font-medium text-secondary-900">
                Modules ({moduleNames.length}):
              </span>
            </div>
            <ul className="list-disc list-inside space-y-1 ml-6">
              {moduleNames.map((name, index) => (
                <li key={index} className="text-sm text-secondary-600">{name}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Features - Always show with star icon */}
        <div className="mb-4">
          <div className="flex items-center text-sm mb-2">
            <Star className="h-4 w-4 text-secondary-400 mr-2" />
            <span className="font-medium text-secondary-900">Features:</span>
          </div>
          {featureNames.length > 0 ? (
            <ul className="list-disc list-inside space-y-1 ml-6">
              {featureNames.map((name, index) => (
                <li key={index} className="text-sm text-secondary-600">{name}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-secondary-600 ml-6">No features included</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

